/**
 * BlastRadiusPanel Component
 *
 * Feature: 178-client-management (US7 — blast radius)
 * Description: Shows the client's footprint — every workspace impacted if this
 *   client's data is at risk (directly-assigned UNION workspaces under assigned
 *   capacities), with per-workspace asset counts and a central-fabric
 *   (serves_all_clients) toggle. The toggle is Admin-only (backend enforces it;
 *   the control is disabled for non-admins). Connecting clients to assets is what
 *   makes the blast-radius tool valuable.
 * Constitution: v1.22.0 — Principle XXXVII (Code Headers), XLV (Design Blueprint)
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, Database, Boxes, AlertCircle, Globe, Link2 } from 'lucide-react';
import { clientApi } from '../../services/clientApi';
import { useAuth } from '../../modules/backup/hooks/useAuth';
import { DualScrollTable } from '../common/TableControls';
import { roleSatisfies } from '../../../../shared/access/roles';

interface BlastRadiusPanelProps {
  clientId: string;
}

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: number }> = ({ icon, label, value }) => (
  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
    <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
      {icon}
      <span className="text-sm">{label}</span>
    </div>
    <p className="mt-1 text-2xl font-semibold text-[var(--color-text-primary)]">{value}</p>
  </div>
);

export const BlastRadiusPanel: React.FC<BlastRadiusPanelProps> = ({ clientId }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = roleSatisfies(user?.role, ['Admin']);

  const { data, isLoading, error } = useQuery({
    queryKey: ['client', clientId, 'blast-radius'],
    queryFn: () => clientApi.getBlastRadius(clientId),
    staleTime: 30_000,
    enabled: !!clientId,
  });

  const toggle = useMutation({
    mutationFn: (vars: { workspaceId: string; value: boolean }) =>
      clientApi.setServesAllClients(vars.workspaceId, vars.value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client', clientId, 'blast-radius'] }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[var(--color-accent)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-red-600 dark:text-red-400">
        <AlertCircle className="mb-2 h-8 w-8" />
        <p>Failed to load blast radius</p>
        <p className="text-sm text-[var(--color-text-secondary)]">{(error as Error).message}</p>
      </div>
    );
  }

  if (!data || data.workspaces.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-[var(--color-text-secondary)]">
        <Boxes className="mb-4 h-12 w-12 opacity-50" />
        <p className="text-lg font-medium">No impacted assets.</p>
        <p className="mt-1 text-sm">This client has no assigned workspaces or capacities yet.</p>
      </div>
    );
  }

  const { workspaces, totals } = data;

  return (
    <div className="space-y-4">
      {/* Totals */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat icon={<FolderOpen className="h-4 w-4" />} label="Workspaces" value={totals.workspaces} />
        <Stat icon={<Database className="h-4 w-4" />} label="Lakehouses" value={totals.lakehouses} />
        <Stat icon={<Boxes className="h-4 w-4" />} label="Warehouses" value={totals.warehouses} />
      </div>

      <p className="text-sm text-[var(--color-text-secondary)]">
        If this client's data is at risk, the assets below are in the blast radius. A{' '}
        <span className="font-medium text-[var(--color-text-primary)]">central fabric</span> workspace serves
        all clients.
      </p>

      {/* Impacted workspaces */}
      <div className="rounded-lg border border-[var(--color-border)]">
        <DualScrollTable>
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">Workspace</th>
                <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">Capacity</th>
                <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">Link</th>
                <th className="px-4 py-3 text-right font-medium text-[var(--color-text-secondary)]">Lakehouses</th>
                <th className="px-4 py-3 text-right font-medium text-[var(--color-text-secondary)]">Warehouses</th>
                <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">Central Fabric</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {workspaces.map((w) => (
                <tr key={w.workspaceId} className="hover:bg-[var(--color-bg-subtle)]">
                  <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4 text-[var(--color-text-secondary)]" />
                      {w.workspaceName}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">{w.capacityName || '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        w.linkType === 'direct'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                          : w.linkType === 'central-fabric'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                          : 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
                      }`}
                    >
                      <Link2 className="h-3 w-3" />
                      {w.linkType === 'direct'
                        ? 'Direct'
                        : w.linkType === 'central-fabric'
                        ? 'Central fabric'
                        : 'Via capacity'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-[var(--color-text-secondary)]">{w.lakehouseCount}</td>
                  <td className="px-4 py-3 text-right text-[var(--color-text-secondary)]">{w.warehouseCount}</td>
                  <td className="px-4 py-3">
                    <label className={`flex items-center gap-2 ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}>
                      <input
                        type="checkbox"
                        checked={w.servesAllClients}
                        disabled={!isAdmin || toggle.isPending}
                        onChange={(e) => toggle.mutate({ workspaceId: w.workspaceId, value: e.target.checked })}
                        className="rounded border-[var(--color-border)] text-[var(--color-accent)] focus:ring-[var(--color-accent)] disabled:opacity-50"
                      />
                      {w.servesAllClients && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-accent)]">
                          <Globe className="h-3 w-3" /> Central
                        </span>
                      )}
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      {toggle.isError && (
        <p className="text-sm text-red-600 dark:text-red-400">{(toggle.error as Error).message}</p>
      )}
    </div>
  );
};

export default BlastRadiusPanel;
